import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-hot-toast';
import { expireSession } from './session';

export default class Request {
  /**
   * Sends an HTTP request using Axios.
   *
   * @param {string} url - The endpoint URL (relative to the base URL).
   * @param {string} method - The HTTP method (e.g., "GET", "POST").
   * @param {Object} [config={}] - Optional configuration for the Axios request (e.g., headers, data).
   * @param {Object} [utils=undefined] - Optional utilities, such as toast messages for success or error notifications.
   * @returns {Promise} - A promise representing the outcome of the HTTP request.
   */
  static async sendRequest(url, method, config = {}, utils = undefined) {
    const base_url = 'https://api.irangardi.app/api/v1/';
    const verb = (method || 'GET').toUpperCase();

    // Public, rarely changing endpoints are served from a persistent cache (stale-while-revalidate)
    const cacheKey = isCacheable(url, verb, config) ? `${verb}:${url}` : null;
    if (cacheKey) {
      const cached = readCache(cacheKey);
      if (cached) {
        if (Date.now() - cached.t > FRESH_MS) revalidate(cacheKey, url, verb, config, base_url);
        return toResponse(cached.data);
      }
      return fetchAndCache(cacheKey, url, verb, config, base_url, utils);
    }

    // Identical GETs that are in flight at the same time (e.g. redux + react-query asking for the same
    // place) share a single network call. Nothing is cached here, so the data is always fresh.
    if (verb === 'GET' && !config.data && !config.params && !utils) {
      const key = `GET:${url}:${Cookies.get('iran_token') || ''}`;
      if (inflight.has(key)) return inflight.get(key);
      const promise = Request.handleRequest(Request.rawRequest(url, verb, config, base_url)).finally(() =>
        inflight.delete(key),
      );
      inflight.set(key, promise);
      return promise;
    }

    return Request.handleRequest(Request.rawRequest(url, verb, config, base_url), utils);
  }

  static rawRequest(url, method, config, base_url) {
    const token = Cookies.get('iran_token');
    return axios.request({
      url: base_url + url,
      method: method,
      headers: {
        // public requests carry no Authorization header at all (it used to be "Bearer undefined")
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...config.headers,
      },
      ...config,
    });
  }

  /**
   * Handles the response of the HTTP request, including success and error handling.
   *
   * @param {Promise} request - The promise representing the HTTP request.
   * @param {Object} [utils=undefined] - Optional utilities, such as success toast notifications.
   * @returns {Promise} - A promise with the response data or an error message.
   */
  static async handleRequest(request, utils = undefined) {
    try {
      const response = await request;

      if (utils?.toastSuccess) {
        toast.success(utils.message);
      }
      return response;
    } catch (err) {
      // `err.response` is undefined on network errors / timeouts – never read it without a guard
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 422 && /login\/(google|apple)$/.test(err?.config?.url || '') && data?.message) {
        toast.error(data.message); // social login: say what is wrong (e.g. client id mismatch) instead of a generic text
      } else if (status === 422) {
        // prefer the first field error (Laravel validation), fall back to the message
        const firstError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
        toast.error(firstError || data?.message);
      }
      if (status === 503 && /login\/(google|apple)$/.test(err?.config?.url || '') && data?.message) toast.error(data.message);
      if (status === 401) {
        // a request that carried a token and was refused = the session is over; without a token it is a failed login
        if (Cookies.get('iran_token')) expireSession();
        else if (/login\/(google|apple)$/.test(err?.config?.url || '')) toast.error('ورود با حساب اجتماعی انجام نشد');
        else toast.error('نام کاربری یا کلمه عبور اشتباه می‌باشد');
      }
      // Only a missing *detail* page (place / post ... "show/{id}") is a "not found" page. A 404 from a
      // secondary widget request (banners, lists, weather ...) must never throw the visitor off the page.
      if (status === 404 && /\/show\/[^/?]+/.test(err?.config?.url || '') && window.location.pathname !== '/not-found') {
        window.location.replace('/not-found');
      }
    }
  }
}

// ***************** Persistent response cache ***************** //
const CACHE_PREFIX = 'irg_api_v1:';
const FRESH_MS = 10 * 60 * 1000; // below this age: no background refresh
const MAX_STALE_MS = 7 * 24 * 60 * 60 * 1000; // older entries are ignored
const MAX_ENTRY_CHARS = 400 * 1024;

const CACHEABLE = [
  /^home\/(slider|banners|types|activity|latest\/place|best\/place|mostView\/place|latest\/post|placeCustomList|subActivity|faq)(\?|$)/,
  /^filter\/(getProvince|getCity)$/,
];

// Per-place payloads contain user-specific flags (bookmark/favourite), so they are cached for guests only
const GUEST_ONLY_CACHEABLE = [/^home\/place\/show\/\d+$/];

const memory = new Map();
const inflight = new Map();

function isCacheable(url, verb, config) {
  if (verb !== 'GET' && !(verb === 'POST' && /^filter\/get(Province|City)$/.test(url))) return false;
  if (config && (config.data || config.params)) return false;
  if (GUEST_ONLY_CACHEABLE.some((re) => re.test(url))) return !Cookies.get('iran_token');
  return CACHEABLE.some((re) => re.test(url));
}

function readCache(key) {
  let entry = memory.get(key);
  if (!entry) {
    try {
      const raw = localStorage.getItem(CACHE_PREFIX + key);
      if (raw) entry = JSON.parse(raw);
    } catch (e) {
      entry = undefined;
    }
    if (entry) memory.set(key, entry);
  }
  if (!entry || Date.now() - entry.t > MAX_STALE_MS) return null;
  return entry;
}

function writeCache(key, data) {
  const entry = { t: Date.now(), data };
  memory.set(key, entry);
  try {
    const raw = JSON.stringify(entry);
    if (raw.length <= MAX_ENTRY_CHARS) localStorage.setItem(CACHE_PREFIX + key, raw);
  } catch (e) {
    // storage full / disabled: the in-memory copy still works for this session
  }
}

// Same shape the callers expect from axios
function toResponse(data) {
  return { data, status: 200, statusText: 'OK', headers: {}, config: {}, fromCache: true };
}

function fetchAndCache(key, url, verb, config, base_url, utils) {
  // identical parallel requests (e.g. footer + page) share one network call
  if (inflight.has(key)) return inflight.get(key);
  const promise = Request.handleRequest(Request.rawRequest(url, verb, config, base_url), utils)
    .then((response) => {
      if (response && response.status === 200 && response.data !== undefined) {
        writeCache(key, response.data);
      }
      return response;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, promise);
  return promise;
}

function revalidate(key, url, verb, config, base_url) {
  if (inflight.has(key)) return;
  fetchAndCache(key, url, verb, config, base_url).catch(() => {});
}
